<mat-card *ngIf="canManage" class="report-subs-card">
  <mat-card-content>
    <div class="header-row">
      <h3>Report Subscriptions</h3>
      <button mat-stroked-button color="primary" *ngIf="!showForm" (click)="startAdd()">
        <mat-icon>add</mat-icon> Add subscription
      </button>
    </div>

    <p class="hint" *ngIf="!allMode">
      Scheduled reports emailed for <strong>{{ companyName || 'this company' }}</strong>.
      Use <em>Send now</em> to run one immediately (e.g. if the scheduled send was missed).
    </p>
    <p class="hint" *ngIf="allMode">
      Every scheduled report, across all companies.
      Use <em>Send now</em> to run one immediately (e.g. if the scheduled send was missed).
    </p>

    <div class="filters" *ngIf="allMode && !showForm">
      <mat-form-field appearance="outline">
        <mat-label>Search</mat-label>
        <input matInput [(ngModel)]="filterText" placeholder="Company, title or recipient">
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Report type</mat-label>
        <mat-select [(ngModel)]="filterType">
          <mat-option [value]="null">All types</mat-option>
          <mat-option *ngFor="let t of reportTypes" [value]="t.value">{{ t.label }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div *ngIf="loading" class="loading">
      <mat-spinner diameter="28"></mat-spinner>
    </div>

    <table mat-table [dataSource]="filteredSubscriptions" class="w-100"
           *ngIf="!loading && filteredSubscriptions.length > 0">
      <ng-container matColumnDef="company">
        <th mat-header-cell *matHeaderCellDef>Company</th>
        <td mat-cell *matCellDef="let sub">
          <a class="link" [routerLink]="['/admin/companies', sub.companyId]">{{ sub.company || sub.companyId }}</a>
        </td>
      </ng-container>

      <ng-container matColumnDef="reportType">
        <th mat-header-cell *matHeaderCellDef>Report</th>
        <td mat-cell *matCellDef="let sub">{{ reportTypeLabel(sub.reportType) }}</td>
      </ng-container>

      <ng-container matColumnDef="enabled">
        <th mat-header-cell *matHeaderCellDef>Enabled</th>
        <td mat-cell *matCellDef="let sub">
          <mat-slide-toggle [checked]="!!sub.enabled" (change)="toggleEnabled(sub)"></mat-slide-toggle>
        </td>
      </ng-container>

      <ng-container matColumnDef="toRecipients">
        <th mat-header-cell *matHeaderCellDef>Recipients</th>
        <td mat-cell *matCellDef="let sub">
          <div>{{ sub.toRecipients || '—' }}</div>
          <div class="cc" *ngIf="sub.ccRecipients">cc: {{ sub.ccRecipients }}</div>
        </td>
      </ng-container>

      <ng-container matColumnDef="templateName">
        <th mat-header-cell *matHeaderCellDef>Template</th>
        <td mat-cell *matCellDef="let sub">{{ templateName(sub.templateName) }}</td>
      </ng-container>

      <ng-container matColumnDef="includes">
        <th mat-header-cell *matHeaderCellDef>Target companies</th>
        <td mat-cell *matCellDef="let sub">
          <div *ngIf="sub.reportTitle" class="cc">as “{{ sub.reportTitle }}”</div>
          {{ additionalCompanyNames(sub) }}
        </td>
      </ng-container>

      <ng-container matColumnDef="lastSentUtc">
        <th mat-header-cell *matHeaderCellDef>Last sent</th>
        <td mat-cell *matCellDef="let sub">
          {{ sub.lastSentUtc ? (sub.lastSentUtc | date:'medium') : 'Never' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="actions-col">Actions</th>
        <td mat-cell *matCellDef="let sub" class="actions-col">
          <button mat-icon-button color="primary" matTooltip="Send now" (click)="sendNow(sub)">
            <mat-icon>send</mat-icon>
          </button>
          <button mat-icon-button matTooltip="Edit" (click)="startEdit(sub)">
            <mat-icon>edit</mat-icon>
          </button>
          <button mat-icon-button color="warn" matTooltip="Delete" (click)="remove(sub)">
            <mat-icon>delete</mat-icon>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>

    <p *ngIf="!loading && filteredSubscriptions.length === 0 && !showForm" class="empty">
      {{ !allMode ? 'No report subscriptions for this company yet.'
        : subscriptions.length ? 'No subscriptions match the filters.' : 'No report subscriptions yet.' }}
    </p>

    <!-- Add / edit form -->
    <form [formGroup]="form" *ngIf="showForm" (ngSubmit)="save()" class="sub-form">
      <h4>{{ editingId ? 'Edit subscription' : 'New subscription' }}</h4>

      <div fxLayout="row wrap" fxLayoutGap="16px grid">
        <mat-form-field appearance="outline" fxFlex="100" *ngIf="allMode">
          <mat-label>Company</mat-label>
          <mat-select formControlName="companyId">
            <mat-option *ngFor="let c of companies" [value]="c.id">{{ c.name }}</mat-option>
          </mat-select>
          <mat-hint>The company the report is for (the supplier, for supplier reports)</mat-hint>
          <mat-error *ngIf="form.controls.companyId.invalid">A company is required</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" fxFlex="48">
          <mat-label>Report type</mat-label>
          <mat-select formControlName="reportType" (selectionChange)="onReportTypeChange()">
            <mat-option *ngFor="let t of reportTypes" [value]="t.value">{{ t.label }}</mat-option>
          </mat-select>
        </mat-form-field>

        <div fxFlex="48" fxLayoutAlign="start center">
          <mat-slide-toggle formControlName="enabled">Enabled</mat-slide-toggle>
        </div>

        <mat-form-field appearance="outline" fxFlex="48">
          <mat-label>To recipients</mat-label>
          <input matInput formControlName="toRecipients" placeholder="a@x.com; b@y.com">
          <mat-hint>Semicolon-separated</mat-hint>
          <mat-error *ngIf="form.controls.toRecipients.invalid">At least one recipient is required</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" fxFlex="48">
          <mat-label>Cc recipients</mat-label>
          <input matInput formControlName="ccRecipients" placeholder="c@z.com">
          <mat-hint>Optional, semicolon-separated</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" fxFlex="48">
          <mat-label>Report template</mat-label>
          <mat-select formControlName="templateName">
            <mat-option *ngFor="let t of templatesForType" [value]="t.slug">{{ t.displayName }}</mat-option>
          </mat-select>
          <mat-error *ngIf="form.controls.templateName.invalid">A template is required</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" fxFlex="48">
          <mat-label>Report URL</mat-label>
          <input matInput formControlName="reportUrl" placeholder="https://…">
          <mat-hint>Optional "View Report" link; leave blank to hide the button</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" fxFlex="48" *ngIf="form.value.reportType !== supplierMonthlyProduct">
          <mat-label>Target companies</mat-label>
          <mat-select formControlName="additionalCompanyIds" multiple>
            <mat-option *ngFor="let c of availableCompanies" [value]="c.id">{{ c.name }}</mat-option>
          </mat-select>
          <mat-hint>Companies this report covers</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" fxFlex="48">
          <mat-label>Report title</mat-label>
          <input matInput formControlName="reportTitle" placeholder="e.g. Guest Group">
          <mat-hint>{{ form.value.reportType === supplierMonthlyProduct
            ? 'Supplier name on the PDF; blank uses the company name'
            : 'Heading shown in the email; blank uses the company name' }}</mat-hint>
        </mat-form-field>
      </div>

      <div class="form-actions">
        <button mat-flat-button color="primary" type="submit" [disabled]="saving">
          {{ editingId ? 'Save changes' : 'Create' }}
        </button>
        <button mat-button type="button" (click)="cancelEdit()" [disabled]="saving">Cancel</button>
      </div>
    </form>
  </mat-card-content>
</mat-card>
